import { Map, AdvancedMarker, MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import s from './Map.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../../../sdk/api';
import { ReactComponent as Star } from '../../../../images/new-icons/yellow-star.svg';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Close } from '../../../../images/new-icons/cross-thin-black.svg';

type Poi = {
  key: string;
  location: google.maps.LatLngLiteral;
  price: number;
  distance?: number;
  salon?: {
    id: string;
    avatar: string;
    first_name: string;
    name: string;
    latitude: number;
    longitude: number;
    full_address: string;
  };
};

const DEFAULT_CENTER = { lat: 25.0933903, lng: 55.153731 };

const VanuesMap = ({
  distances,
  data,
  setDistances,
  selectedMenuCategory,
  productId,
  brand,
  minPrice,
  maxPrice,
  cityId,
  isMapExpanded,
  setIsMapExpanded,
  onMarkerClick,
  clearSelectedSalon,
  minRating,
  debouncedSearchText,
}) => {
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral | undefined>();
  const [position, setPosition] = useState<google.maps.LatLngLiteral>(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(13);
  const [selectedSalon, setSelectedSalon] = useState<Poi | null>(null);
  const [salonReviews, setSalonReviews] = useState<Record<string, any[]>>({});
  const navigate = useNavigate();
  const [userMovedMap, setUserMovedMap] = useState(false);

  const locations = useMemo<Poi[]>(() => {
    if (!data) return [];
    
    const uniqueLocations: Poi[] = [];
    const seenIds = new Set();
    
    data.forEach(item => {
      if (item?.salon?.id && !seenIds.has(item.salon.id)) {
        seenIds.add(item.salon.id);
        uniqueLocations.push({
          price: item?.price || item?.min_price,
          key: item?.salon?.id,
          location: {
            lat: item?.salon?.latitude || 0,
            lng: item?.salon?.longitude || 0,
          },
        });
      }
    });
    
    return uniqueLocations;
  }, [data]);

  useEffect(() => {
    // Check if we have stored location in localStorage
    const storedLocation = localStorage.getItem('userLocation');

    if (storedLocation) {
      try {
        const parsedLocation = JSON.parse(storedLocation);
        setUserLocation(parsedLocation);
        setPosition(parsedLocation);
      } catch (error) {
        console.error('Error parsing stored location:', error);
        requestGeolocation();
      }
    } else {
      requestGeolocation();
    }

    function requestGeolocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            // Store location in localStorage to avoid asking again
            localStorage.setItem('userLocation', JSON.stringify(location));
            setUserLocation(location);
            setPosition(location);
          },
          error => {
            console.error('Ошибка при получении местоположения:', error);
            const fallbackLocation = { lat: 25.0933903, lng: 55.153731 };
            setUserLocation(fallbackLocation);
            setPosition(fallbackLocation);
          },
        );
      } else {
        console.error('Geolocation API не поддерживается этим браузером');
        const fallbackLocation = { lat: 25.0933903, lng: 55.153731 };
        setUserLocation(fallbackLocation);
        setPosition(fallbackLocation);
      }
    }
  }, []);

  useEffect(() => {
    if (userLocation && locations?.length > 0) {
      const calculatedDistances = locations?.map(poi => {
        const distance = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(userLocation.lat, userLocation.lng),
          new google.maps.LatLng(poi.location.lat, poi.location.lng),
        );
        return { id: poi.key, distance: distance / 1000 };
      });
      setDistances(calculatedDistances);
    }
  }, [userLocation, locations]);

  useEffect(() => {
    setUserMovedMap(false);
  }, [
    selectedMenuCategory,
    productId,
    brand,
    minPrice,
    maxPrice,
    cityId,
    minRating,
    debouncedSearchText,
  ]);

  useEffect(() => {
    if (
      (cityId ||
        selectedMenuCategory ||
        productId ||
        brand ||
        minPrice !== undefined ||
        maxPrice !== undefined ||
        minRating > 0 ||
        debouncedSearchText) &&
      locations?.length > 0
    ) {
      if (!userMovedMap && locations[0]?.location) {
        setPosition(locations[0].location);
        setZoom(13);
      }
    }
  }, [
    selectedMenuCategory,
    productId,
    brand,
    minPrice,
    maxPrice,
    cityId,
    locations,
    minRating,
    userMovedMap,
    debouncedSearchText,
  ]);

  useEffect(() => {
    const fetchAllReviews = async () => {
      const reviewsPromises = data?.map(async item => {
        const { data: reviews } = await supabase
          .from('reviews')
          .select('*')
          .eq('salon', item?.salon?.id);
        return { id: item?.salon?.id, reviews };
      });

      const allReviews = await Promise.all(reviewsPromises || []);
      const reviewsMap = allReviews.reduce((acc, { id, reviews }) => {
        acc[id] = reviews;
        return acc;
      }, {});

      setSalonReviews(reviewsMap);
    };

    fetchAllReviews();
  }, [data]);

  const getAverageRating = (salonId: string) => {
    const reviews = salonReviews[salonId] || [];
    if (!reviews.length) return 0;
    const total = reviews.reduce((acc, review) => acc + review.rank, 0);
    return (total / reviews.length).toFixed(1);
  };

  const handleMarkerClick = salon => {
    const calculatedDistance = distances?.find(d => d.id === salon?.salon?.id)?.distance;
    const salonWithDistance = {
      ...salon,
      distance: calculatedDistance,
    };
    onMarkerClick(salonWithDistance);
    setSelectedSalon(salonWithDistance);
  };

  return (
    <div
      className={clsx(s.map, isMapExpanded && s.expanded)}
      onClick={() => !isMapExpanded && setIsMapExpanded(true)}
    >
      <Map
        zoom={zoom}
        center={position}
        mapId={'661e50b2cb212eb1'}
        zoomControl={false}
        scaleControl={false}
        streetViewControl={false}
        mapTypeControl={false}
        fullscreenControl={false}
        onCameraChanged={(ev: MapCameraChangedEvent) => {
          if (ev.detail.center && ev.detail.zoom) {
            setPosition(ev.detail.center);
            setZoom(ev.detail.zoom);
            setUserMovedMap(true);
          }
        }}
        gestureHandling={'greedy'}
      >
        <>
          <AdvancedMarker position={userLocation}>
            <div className={s.custom_marker}>My Location</div>
          </AdvancedMarker>
          {locations?.map((poi: Poi) => (
            <AdvancedMarker
              key={poi.key}
              position={poi?.location}
              onClick={(e: google.maps.MapMouseEvent) => {
                e.domEvent?.stopPropagation();
                handleMarkerClick(data.find(item => item.salon.id === poi.key));
              }}
            >
              <div className={s.custom_marker}>
                {salonReviews[poi.key]?.length ? (
                  <>
                    <Star /> {getAverageRating(poi.key)}
                  </>
                ) : (
                  'No reviews'
                )}
              </div>
            </AdvancedMarker>
          ))}
          {selectedSalon && (
            <div className={s.salon_card_container}>
              <div
                className={s.salon_card}
                onClick={() => navigate(`/salon/${selectedSalon?.salon?.id}`)}
              >
                <img
                  className={s.salon_card_image}
                  src={selectedSalon?.salon?.avatar}
                  alt={selectedSalon?.salon?.first_name}
                />
                <div className={s.salon_card_info}>
                  <div className={s.salon_card_info_header}>
                    <div className={s.salon_card_info_header_left}>
                      <img
                        className={s.salon_card_info_avatar}
                        src={selectedSalon?.salon?.avatar}
                        alt={selectedSalon?.salon?.first_name}
                      />
                      <div className={s.salon_card_info_name}>{selectedSalon?.salon?.name}</div>
                    </div>
                    <div className={s.salon_card_info_distance}>
                      {selectedSalon?.distance
                        ? `${selectedSalon.distance.toFixed(0)} km `
                        : 'Loading...'}
                    </div>
                  </div>
                  <div className={s.salon_card_info_reviews}>
                    {salonReviews[selectedSalon?.salon?.id || '']?.length ? (
                      <>
                        <div className={s.salon_card_info_reviews_stars}>
                          <Star /> {getAverageRating(selectedSalon?.salon?.id || '')}
                        </div>
                        <div className={s.salon_card_info_reviews_count}>
                          ({salonReviews[selectedSalon?.salon?.id || '']?.length})
                        </div>
                      </>
                    ) : (
                      <div className={s.salon_card_info_reviews_no_reviews}>No reviews</div>
                    )}
                  </div>
                  <div className={s.salon_card_info_place}>
                    {selectedSalon?.salon?.full_address}
                  </div>
                </div>
              </div>
            </div>
          )}
          {isMapExpanded && (
            <div
              className={s.salon_button}
              onClick={() => {
                setIsMapExpanded(false);
                setSelectedSalon(null);
                clearSelectedSalon();
              }}
            >
              <Close className={s.salon_button_icon} />
            </div>
          )}
        </>
      </Map>
    </div>
  );
};

export default VanuesMap;
